class ProductSearchResult {
  page = 0
  pageCount = 0
  hitCount = 0
  special = []
  categories = []
  hits = []
  retiredCount = 0
}

class ProductSearch extends EventTarget {
  static SORT_FEATURED = "featured"

  baseIndexName = "shop.search.products"
  algolia = algoliasearch.algoliasearch("M00M3RFW1A", "97780d6ca6d719cafd5ef2556417e2ce")
  requestCount = 0

  page = 1
  pageSize = 48
  query = ""
  sort = ProductSearch.SORT_FEATURED
  // the collection every result must belong to, kept apart from `collection` so a department can be chosen within it
  scope
  // the facet the scope names: a collection by default, or hierarchy.special for a view like Popular
  scopeFacet = "hierarchy.lvl0"
  collection
  special
  category
  subCategory
  vendor
  inStockOnly
  includeRetired
  // across the catalogue: the hierarchy is every collection with its categories, unmoved by the collection and category chosen
  staticHierarchy = false

  static STATE_FIELDS = ["scope", "collection", "special", "category", "subCategory", "vendor", "inStockOnly", "includeRetired", "query", "sort", "page"]

  constructor() {
    super()
  }

  // the filters and position as a plain object, so a view can be stashed and put back
  get state() {
    return Object.fromEntries(ProductSearch.STATE_FIELDS.map(f => [f, this[f]]))
  }

  set state(state) {
    for(let field of ProductSearch.STATE_FIELDS) {
      if(field in state) this[field] = state[field]
    }
  }

  setSpecial(special) {
    this.special = special ? special : "All"
    this.category = null
    this.subCategory = null
    this.page = 1
    this.search()
  }

  setCategory(category) {
    this.category = category
    this.subCategory = null
    this.page = 1
    this.search()
  }

  setSubCategory(subCategory, category) {
    this.category = category
    this.subCategory = subCategory
    this.page = 1
    this.search()
  }

  search() {
    var requestId = ++this.requestCount

    let indexName = this.baseIndexName
    if(this.sort != ProductSearch.SORT_FEATURED) {
      indexName = `${indexName}.sort-${this.sort}`
    }

    let filters = []
    let facetFilters = []

    let scopeFilter = this.scope ? [`${this.scopeFacet}:${this.scope}`] : []
    filters.push(...scopeFilter)

    if(this.collection) {
      filters.push(`hierarchy.lvl0:${this.collection}`)
    }

    let stockFilter = this.inStockOnly ? ["offer.in_stock:true"] : []
    filters.push(...stockFilter)

    // the retired filter is applied to the hits but kept out of the retired count query
    let retiredFilter = this.includeRetired ? [] : ["offer.retired:false"]

    // the view counts reflect the collection and stock filters but not the view itself; with a static hierarchy only the scope and stock filters
    var viewFilters = this.staticHierarchy ? [...scopeFilter, ...stockFilter, ...retiredFilter] : [...filters, ...retiredFilter]

    if(this.special) {
      filters.push(`hierarchy.special:${ this.special }`)
    }

    if(this.subCategory) {
      facetFilters.push(`hierarchy.lvl2:${this.collection} -> ${ this.category } -> ${ this.subCategory }`)
    } else if(this.category) {
      facetFilters.push(`hierarchy.lvl1:${this.collection} -> ${ this.category }`)
    }

    if(this.vendor) {
      filters.push(`product.vendor:${this.vendor}`)
    }

    let queries = [
      { // query to get hits based on the users current search and filters
        indexName: indexName, query: this.query,
        params: {
          facetFilters: [...filters, ...retiredFilter, ...facetFilters], hitsPerPage: this.pageSize, page: this.page - 1,
          attributesToRetrieve: ["id", "title", "sku", "offer", "product.id", "product.variant_count", "product.handle", "product.title", "product.teaser", "product.vendor", "tags", "image", "reviews", "offer", "hierarchy.special"]
      }},
      { // query to get category facets: the collection's categories and sub categories, or the collections themselves with their categories
        indexName: indexName, query: this.query,
        params: {
          facetFilters: this.staticHierarchy ? [...scopeFilter, ...stockFilter, ...retiredFilter] : [...filters, ...retiredFilter], hitsPerPage: 0, maxValuesPerFacet: 1000, sortFacetValuesBy: "alpha",
          facets: this.collection && !this.staticHierarchy ? ["hierarchy.lvl1", "hierarchy.lvl2"] : ["hierarchy.lvl0", "hierarchy.lvl1"]
      }},
      { // query to get special facets
        indexName: indexName, query: this.query,
        params: {
          facetFilters: viewFilters, hitsPerPage: 0, maxValuesPerFacet: 1000, sortFacetValuesBy: "alpha",
          facets: ["hierarchy.special"]
      }},
      { // query to count retired products at the current position, whether or not they are shown
        indexName: indexName, query: this.query,
        params: {
          facetFilters: [...filters, ...facetFilters], hitsPerPage: 0,
          facets: ["offer.retired"]
      }}
    ]

    this.algolia.search(queries).then(({results}) => {

      // a more recent search has been done so discard these results
      if(requestId != this.requestCount) {return}

      let result = new ProductSearchResult()
      result.hits = results[0].hits
      result.hitCount = results[0].nbHits
      result.page = results[0].page
      result.pageCount = results[0].nbPages

      // build the categories parent/child hierarchy

      // first create the categories
      result.categories = []
      if(results[1].facets["hierarchy.lvl0"]) {
        for(let [collectionName, value] of Object.entries(results[1].facets["hierarchy.lvl0"])) {
          result.categories.push({name: collectionName, count: value, children: []})
        }
        result.categories.sort((a, b) => b.count - a.count)
      }
      // within a collection its categories are the top level; across the catalogue they sit under their collections
      if(results[1].facets["hierarchy.lvl1"]) {
        for(let [key, value] of Object.entries(results[1].facets["hierarchy.lvl1"])) {
          let [collectionName, categoryName] = key.split(" -> ")
          if(this.collection && !this.staticHierarchy) {
            if(collectionName == this.collection) {
              result.categories.push({
                name: categoryName,
                count: value,
                children: []
              })
            }
          } else {
            let collection = result.categories.find(c => c.name == collectionName)
            if(collection) {
              collection.children.push({name: categoryName, count: value})
            }
          }
        }
      }

      // then add the sub categories to each category
      if(results[1].facets["hierarchy.lvl2"]) {
        for(let [key, value] of Object.entries(results[1].facets["hierarchy.lvl2"])) {
          let [collectionName, categoryName, subCategoryName] = key.split(" -> ")
          if(collectionName == this.collection) {
            let category = result.categories.find(c => c.name == categoryName)
            if(category) {
              category.children.push({name: subCategoryName, count: value})
            }
          }
        }
      }

      // add special filters
      result.special = []
      if(results[2].facets["hierarchy.special"]) {
        for(let [key, value] of Object.entries(results[2].facets["hierarchy.special"])) {
          result.special.push({
            name: key,
            count: value,
            children: []
          })
        }
      }

      result.retiredCount = (results[3].facets["offer.retired"] || {})["true"] || 0

      this.dispatchEvent(new CustomEvent("results", {detail: result}))
    })
  }
}


createSpecialFiltersHtml = (result, search) => {
  let specialFiltersHtml = ""
  for(let special of result.special) {
    let isSelected = special.name == search.special
    let isDimmed = !isSelected
    specialFiltersHtml += `<li data-name="${special.name}" class="${isDimmed ? "dimmed" : ""} ${isSelected ? "selected" : ""}"><div><span>${ special.name }</span><small>${ special.count }</small></div></li>`
  }
  return specialFiltersHtml
}

// `selected` names the chosen category and sub category; `opened` the categories whose children are listed
createCategoryFiltersHtml = (result, selected, opened = []) => {
  let categoryFiltersHtml = ""
  for(let category of result.categories) {
    let isCategorySelected = category.name == selected.category
    let isCategoryDimmed = selected.category && !isCategorySelected
    let subCategoryFiltersHtml = ""
    if(category.name == selected.category || opened.includes(category.name)) {
      subCategoryFiltersHtml += "<ul>"
      for(let subCategory of category.children) {
        let isSubCategorySelected = subCategory.name == selected.subCategory && category.name == selected.category
        // sub categories sit knocked back until one of them is chosen
        let isSubCategoryDimmed = !isSubCategorySelected
        subCategoryFiltersHtml += `<li data-name="${subCategory.name}" data-parent="${category.name}" class="${isSubCategoryDimmed ? "dimmed" : ""} ${isSubCategorySelected ? "selected" : ""}"><div><span>${ subCategory.name }</span><small>${ subCategory.count }</small></div></li>`
      }
      subCategoryFiltersHtml += "</ul>"
    }

    categoryFiltersHtml += `<li data-name="${category.name}" class="${isCategoryDimmed ? "dimmed" : ""} ${isCategorySelected ? "selected" : ""}"><div><span>${ category.name }</span><small>${ category.count }</small></div>${subCategoryFiltersHtml}</li>`
  }

  return categoryFiltersHtml
}

createHitHtml = (hit) => {
  // the visitor's market price; with none in the index the tile shows no price, since the shop price is in another currency
  let offer = window.market && hit.offer.prices ? hit.offer.prices[window.market.handle] : null

  let imageUrl = hit.image
  imageUrl = imageUrl.substring(0, imageUrl.lastIndexOf(".")) + "_300x300" + imageUrl.substring(imageUrl.lastIndexOf("."))

  let isNew = hit.hierarchy.special?.includes("New")
  let isPopular = hit.hierarchy.special?.includes("Popular")
  let isOutOfStock = !hit.offer.in_stock
  let isPreOrder = isOutOfStock && hit.offer.pre_order
  let isRetired = hit.offer.retired
  let isDiscounted = hit.offer.discount_percent != null

  return `
  <article class="result" data-variant="${hit.id}">
    <a href="${shopRoot}/products/${hit.product.handle}?variant=${hit.id}">
      <div class="image">
        <product-badge discount="${hit.offer.discount_percent}" class="${isPreOrder ? "_preorder" : ""} ${isOutOfStock ? "_outofstock": ""} ${isPopular ? "_popular": ""} ${isNew ? "_new": ""} ${isRetired ? "_retired": ""} ${isDiscounted ? "_discounted": ""}"></product-badge>
        <img src="${imageUrl}" alt="${hit.title}" />
        ${hit.reviews.count ? `<span class="rating"><star-rating rating="${hit.reviews.average_rating}" rating_count="${hit.reviews.count}"></star-rating></span>` : ""}
      </div>
      <div class="details">
        <h4>
          ${hit.product.title}
          ${hit.title && (hit.product.variant_count > 1) ? `<small>(${hit.title})</small>` : ""}
        </h4>
        <p>${hit.product.teaser}</p>
      </div>
    </a>
    <footer>
      ${!isOutOfStock || isPreOrder ? createTileCartButtonHtml(isPreOrder ? "Pre-order" : "Add") : ""}
      ${offer ? `<product-price id="${hit.id}" price="${offer.price}" compare_at_price="${offer.compare_at_price || ""}" taxable="${hit.offer.taxable || ""}"></product-price>` : ""}
    </footer>
  </article>`
}

/*
  a tile for an item from the wishlist service, which the wishlist and the cart's
  recommendations share: id (variant), handle, product_title, variant_title,
  description_first_paragraph, image, price_ex_vat, compare_price_ex_vat, taxable,
  in_stock, pre_order and tags. options.label names the cart button
*/
const createItemTileHtml = (item, options = {}) => {
  const escape = (text) => $("<div>").text(text || "").html()
  let image = item.image.replace(/(\.[a-z]+)(\?|$)/, "_512x512$1$2")
  let isPreOrder = item.in_stock && item.pre_order
  let badge = `<product-badge class="${!item.in_stock ? "_outofstock" : ""} ${isPreOrder ? "_preorder" : ""} ${item.tags.includes("_new") ? "_new" : ""}"></product-badge>`
  return `
  <article class="result" data-variant="${item.id}">
    <a href="${shopRoot}/products/${item.handle}?variant=${item.id}">
      <div class="image">
        ${badge}
        <img src="${image}" alt="${escape(item.product_title)}" loading="lazy" />
      </div>
      <div class="details">
        <h4>${escape(item.product_title)}${item.variant_title ? ` <small>(${escape(item.variant_title)})</small>` : ""}</h4>
        <p>${escape(item.description_first_paragraph)}</p>
      </div>
    </a>
    <footer>
      ${item.in_stock ? createTileCartButtonHtml(isPreOrder ? "Pre-order" : (options.label || "Add")) : "<span></span>"}
      <product-price price="${item.price_ex_vat}" compare_at_price="${item.compare_price_ex_vat || ""}" taxable="${item.taxable}"></product-price>
    </footer>
  </article>`
}

// the small cart button in a tile's footer, with its added and refused labels
const createTileCartButtonHtml = (label) => `
  <button type="button" class="add" title="Add to cart">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M1.5 2.5H4.8l2.6 13.2h12.4l2.4-9.6H6.3" /><circle cx="8.6" cy="20.6" r="1.9" /><circle cx="18.2" cy="20.6" r="1.9" /></svg>
    <span class="label">${label}</span><span class="done">Added</span><span class="refusal">None left in stock</span>
  </button>`

/*
  every tile's cart button adds one of its variant. The tile is told with a
  `tileadded` event so a page can react, as the wishlist does by letting go of the item
*/
$(document).on("click", ".result footer .add", (e) => {
  let button = $(e.currentTarget)
  let tile = button.closest(".result")
  $.post(`${shopRoot}/cart/add.js`, {items: [{id: tile.data("variant"), quantity: 1}]}, "json").done(() => {
    button.addClass("added")
    setTimeout(() => button.removeClass("added"), 2000)
    $(window).trigger("cartchanged")
    tile.trigger("tileadded")
  }).fail(() => {
    button.addClass("refused")
    setTimeout(() => button.removeClass("refused"), 2500)
  })
})

